import { useCallback, useEffect, useRef, useState } from "react";

import { PrestazioneRepository } from "core/data/index";

import { IGetPrestazioniRequest, IPrestazione } from "core/interfaces/data/IPrestazioneRequest";

import { CustomRadio, CustomButton } from "core/interfaces/components/index";
import { Radio, Skeleton, Space, Form, Alert, Select, Spin } from "antd";

import "./Prestazione.css";
import { CustomButtonType } from "components/CustomButton/CustomButtonType";
import i18n from "utils/i18n";
import { setPrestazione, setTariffa } from "core/redux/features/dataForm/dataFormSlice";

import { IOption } from "core/interfaces/data/IOption";
import NavigationManager from "utils/NavigationManager";
import { StorageHelper } from "utils/SessionStorageHelper";
import IPrestazioneProps from "../IPrestazioneProps";
import { Enhanced } from "utils/ReactUtils";
import CustomTranslation from "components/CustomTranslation/CustomTranslation";

const { Option } = Select;

const Prestazione: React.FC<IPrestazioneProps> = ({ t, sessionInfo, dispatch, dataForm }) => {
	const startAreaRef: any = useRef();

	const startRef: any = useRef(); // aggiunta $ALC

	const { MoveStep } = NavigationManager(sessionInfo);
	const [prestazioni, setPrestazioni] = useState<IPrestazione[] | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(true);
	const [showSpinner, setShowSpinner] = useState<boolean>(false);

	const [macroPrestazioni, setmacroPrestazioni] = useState<IOption[]>([]);
	const [prestazioneDefault, setPrestazioneDefault] = useState<number | undefined>();
	const [prestazioneNonValida, setPrestazioneNonValida] = useState<boolean>(false);

	const [formItemSelected, setFormItemSelected] = useState<boolean>(false);

	const fetchMacroPrestazioni = useCallback(async (): Promise<IOption[]> => {
		const mp = await PrestazioneRepository.getMacroPrestazioni();
		if (mp) {
			return mp;
		}
		return [];
	}, []);

	const SetPrestazione = (prestazione: IPrestazione) => {
		dispatch(setPrestazione(prestazione));
		dispatch(setTariffa(undefined));
		MoveStep("next");
	};

	const getActivityOwner = useCallback((macroPrest: IOption[]) => {
		return macroPrest.filter((el: IOption) => el.key?.toString() === StorageHelper.getItem("activity-owner")).length > 0
			? StorageHelper.getItem("activity-owner")
			: undefined;
	}, []);

	const getDefaultActivity = useCallback(() => {
		var prestazioneDefault =
			StorageHelper.getItem("default-activity") !== undefined ? Number.parseInt(StorageHelper.getItem("default-activity")!) : undefined;

		return prestazioneDefault;
	}, []);

	useEffect(() => {
		if (startRef.current) startRef.current.focus();
	}, []);

	useEffect(() => {
		async function getData() {
			let areaRequestProps: IGetPrestazioniRequest = {
				IdEquipe: dataForm.area?.IdEquipe!,
				CodGruppo: dataForm.area?.CodGruppo!,
				culture: i18n.language,
				IdSessione: sessionInfo.sessione!.idSessione!,
				step: sessionInfo.step as number,
			};
			var macro = await fetchMacroPrestazioni();

			areaRequestProps.ActivityOwner = Number.parseInt(getActivityOwner(macro) ?? "");
			setLoading(true);
			let data = await PrestazioneRepository.getPrestazioni(areaRequestProps);
			setPrestazioni(data);

			setmacroPrestazioni(macro);
			if (data && getDefaultActivity()) {
				let prest = data.filter((p) => {
					return p.IguAttivita === getDefaultActivity();
				})[0];

				if (!prest) {
					if (!dataForm.prestazione) setPrestazioneNonValida(true);
				} else {
					setPrestazioneDefault(prest.IguAttivita);
					setFormItemSelected(true);
				}
			}

			setShowSpinner(false);
			setLoading(false);
		}
		getData();
		setFormItemSelected(dataForm.prestazione !== undefined);
	}, [
		dataForm.area?.CodGruppo,
		dataForm.area?.IdEquipe,
		dataForm.prestazione,
		fetchMacroPrestazioni,
		getActivityOwner,
		getDefaultActivity,
		sessionInfo.sessione,
		sessionInfo.step,
		t,
	]);

	const OnSubmit = (data: any) => {
		if (prestazioni) {
			let prestazione = prestazioni.filter((prestazione) => {
				return prestazione.IguAttivita === data.prestazione;
			})[0];

			SetPrestazione(prestazione);
		}
	};

	if (loading && !showSpinner) {
		return <Skeleton active />;
	}

	const onChange = (value: string) => {
		if (value) {
			StorageHelper.setItem("activity-owner", value);
		} else {
			StorageHelper.removeItem("activity-owner");
		}
		setShowSpinner(true);
	};
	const onValuesChange = (changedValues: any, values: any) => {
		setFormItemSelected(true);
	};

	return (
		<div id="prestazione" role="main" aria-label="Step 3">
			{/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
			<div className="title item" tabIndex={0} ref={startAreaRef}>
				<span>{t("prestazione.title")}</span>
			</div>

			{prestazioni ? (
				<Form
					onFinish={OnSubmit}
					initialValues={{
						prestazione: dataForm.prestazione ? dataForm.prestazione.IguAttivita : prestazioneDefault,
					}}
					onValuesChange={onValuesChange}
				>
					{prestazioneNonValida && (
						<Alert message={<CustomTranslation t={t} type="phone" i18Key={"prestazione.popup.text"} />} type="warning" showIcon />
					)}
					<div className="text item">
						<span>{t("prestazione.text.0")}</span>
					</div>
					<div className="item">
						<Select
							placeholder={t("prestazione.ricercaMacroCatgoria")}
							style={{ width: "100%" }}
							onChange={onChange}
							defaultValue={getActivityOwner(macroPrestazioni)}
							allowClear
						>
							{macroPrestazioni.map((macroPrestazione) => {
								return (
									<Option key={macroPrestazione.key} value={macroPrestazione.value}>
										{macroPrestazione.label}
									</Option>
								);
							})}
						</Select>
					</div>
					<Spin spinning={showSpinner}>
						<div className="prestazioni-box item">
							<Form.Item
								className={"custom-validation-error"}
								name="prestazione"
								rules={[
									{
										required: true,
										message: t("prestazione.messages.required"),
									},
								]}
							>
								{prestazioni.length > 0 ? (
									<Radio.Group defaultValue={dataForm.prestazione ? dataForm.prestazione.IguAttivita : prestazioneDefault}>
										<Space direction="vertical" className="prestazioni-list">
											{prestazioni.map((prestazione, index) => {
												return <CustomRadio key={index} text={prestazione.Attivita} value={prestazione.IguAttivita} />;
											})}
										</Space>
									</Radio.Group>
								) : (
									<p>{t("prestazione.notFound")}</p>
								)}
							</Form.Item>
						</div>
					</Spin>
					<div className="button-box item">
						{prestazioni.length > 0 ? (
							<>
								<CustomButton text={t("backButton")} OnClick={() => MoveStep("prev")} buttonType={CustomButtonType.negative} />
								<CustomButton text={t("prestazione.button")} htmlType="submit" buttonType={CustomButtonType.positive} enabled={formItemSelected} />
							</>
						) : (
							<CustomButton text={t("backButton")} OnClick={() => MoveStep("prev")} buttonType={CustomButtonType.negative} />
						)}
					</div>
				</Form>
			) : (
				<Alert message={t("errorMessages.base")} type="error" showIcon banner />
			)}
		</div>
	);
};

export default Enhanced(Prestazione, { componentNamespace: "Prestazione" });
